import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { Link } from 'react-router-dom';
import { Topheader } from '../../components/Topheader';
import { ANNOUNCEMENT_UPDATED_EVENT } from '../../components/Announcement/Announcement';
import { DataGetApi, DataPutApiV2 } from '../../helpers/PostDataApi';
import config from '../../constants/config';
import './AnnouncementGroups.css';

const getToken = () => JSON.parse(localStorage.getItem('user') || '{}')?.token;

export const sampleGroupCsv = 'group1,group2,group3,\n1234,5678,9101,\n,1243,,\n,2525,,\n';

const renderAnnouncementStatus = (group) => {
  if (!group.announcement) {
    return <span className="announcementMuted">Not set up</span>;
  }

  if (group.announcement.announcement?.enabled === false) {
    return <span className="announcementDisabled">Disabled</span>;
  }

  return (
    <span className="announcementActiveStatus">
      <CheckCircleIcon className="announcementActiveIcon" titleAccess="Active announcement" />
      Active
    </span>
  );
};

const parseCsvLine = (line) => {
  const values = [];
  let value = '';
  let isQuoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"' && line[index + 1] === '"' && isQuoted) {
      value += '"';
      index += 1;
    } else if (character === '"') {
      isQuoted = !isQuoted;
    } else if (character === ',' && !isQuoted) {
      values.push(value.trim());
      value = '';
    } else {
      value += character;
    }
  }

  values.push(value.trim());
  return values;
};

export const parseGroupCsv = (text) => {
  const rows = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter(Boolean)
    .map(parseCsvLine);

  if (!rows.length) {
    throw new Error('The CSV file is empty.');
  }

  const headerRow = rows[0];
  while (headerRow.length && !headerRow[headerRow.length - 1]) {
    headerRow.pop();
  }
  if (!headerRow.length || headerRow.some((groupName) => !groupName)) {
    throw new Error('The first row must contain a name for every group column.');
  }

  const groupNames = new Set();
  headerRow.forEach((groupName) => {
    if (groupNames.has(groupName)) {
      throw new Error(`Group ${groupName} is listed more than once.`);
    }
    groupNames.add(groupName);
  });

  const groups = headerRow.map((name) => ({ name, locationIds: [] }));
  const assignedLocations = new Set();

  rows.slice(1).forEach((row, rowIndex) => {
    if (row.slice(headerRow.length).some(Boolean)) {
      throw new Error(`Row ${rowIndex + 2} contains more location IDs than group columns.`);
    }

    headerRow.forEach((_groupName, columnIndex) => {
      const locationId = row[columnIndex];
      if (!locationId) return;
      if (assignedLocations.has(locationId)) {
        throw new Error(`Location ${locationId} is assigned more than once.`);
      }
      assignedLocations.add(locationId);
      groups[columnIndex].locationIds.push(locationId);
    });
  });

  if (!groups.some((group) => group.locationIds.length)) {
    throw new Error('The CSV file has no group assignments.');
  }

  return groups;
};

const AnnouncementGroups = () => {
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [csvGroups, setCsvGroups] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvMessage, setCsvMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const loadGroups = () => {
    setIsLoading(true);
    return DataGetApi(`${config.BASEURL}announcement/groups`, {}, getToken()).then((response) => {
      if (response?.status === 200 && Array.isArray(response.data?.groups)) {
        setGroups(response.data.groups);
        setError('');
      } else {
        setError('Unable to load announcement groups.');
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const handleCsvChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setCsvMessage('');
    setCsvGroups(null);
    try {
      setCsvGroups(parseGroupCsv(await file.text()));
    } catch (parseError) {
      setCsvMessage(parseError.message || 'Unable to read this CSV file.');
    }
  };

  const uploadGroups = async () => {
    if (!csvGroups) return;
    setIsUploading(true);
    setCsvMessage('');
    try {
      const response = await DataPutApiV2(`${config.BASEURL}announcement/groups`, { groups: csvGroups });
      if (response?.status === 200) {
        setCsvMessage('Groups updated successfully.');
        setCsvGroups(null);
        setCsvFileName('');
        window.dispatchEvent(
          new CustomEvent(ANNOUNCEMENT_UPDATED_EVENT, {
            detail: { enabled: false },
          })
        );
        await loadGroups();
      } else {
        setCsvMessage('Unable to update announcement groups.');
      }
    } catch (uploadError) {
      const missingMids = uploadError.response?.data?.mids;
      setCsvMessage(
        missingMids?.length
          ? `These MIDs were not found: ${missingMids.join(', ')}.`
          : uploadError.response?.data?.message || 'Unable to update announcement groups.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="rightPanel">
      <Topheader />
      <main className="contentPart announcementGroupsPage">
        <div className="announcementPageHeading">
          <div>
            <Typography component="h1" variant="h5">
              Announcement groups
            </Typography>
            <Typography color="textSecondary" variant="body2">
              Manage which locations receive each announcement.
            </Typography>
          </div>
        </div>
        {isLoading && <CircularProgress aria-label="Loading announcement groups" />}
        {error && (
          <Typography color="error" role="alert">
            {error}
          </Typography>
        )}
        {!isLoading && !error && (
          <TableContainer component={Paper} className="announcementGroupsTable">
            <Table aria-label="Announcement groups">
              <TableHead>
                <TableRow>
                  <TableCell>Group</TableCell>
                  <TableCell>Locations</TableCell>
                  <TableCell>Active announcement</TableCell>
                  <TableCell align="right">Open</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <TableRow hover key={group._id}>
                    <TableCell>
                      <Link className="announcementTableLink" to={`/Announcement/groups/${group._id}`}>
                        {group.name}
                      </Link>
                    </TableCell>
                    <TableCell>{group.locations?.length || 0}</TableCell>
                    <TableCell>{renderAnnouncementStatus(group)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label={`Open ${group.name}`}
                        component={Link}
                        to={`/Announcement/groups/${group._id}`}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!groups.length && (
                  <TableRow>
                    <TableCell colSpan={4}>No announcement groups found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Paper className="announcementGroupsUpload">
          <Typography component="h2" variant="h6">
            Upload group assignments
          </Typography>
          <Typography color="error" variant="body2">
            Warning: uploading new groups will disable all announcements until you publish a new announcement in each
            group.
          </Typography>
          <Typography color="textSecondary" variant="body2">
            CSV columns are group names. Add each MID under its group; empty cells are ignored and MIDs not listed will
            be placed in unclassified. Announcement history is preserved for MIDs that are moved or removed.
          </Typography>
          <div className="announcementGroupsUploadActions">
            <Button
              component="a"
              download="announcement-groups-sample.csv"
              href={`data:text/csv;charset=utf-8,${encodeURIComponent(sampleGroupCsv)}`}
              startIcon={<CloudDownloadIcon />}
              variant="outlined"
            >
              Download sample CSV
            </Button>
            <Button component="label" startIcon={<CloudUploadIcon />} variant="outlined">
              <span>Choose CSV</span>
              <input accept=".csv,text/csv" hidden onChange={handleCsvChange} type="file" />
            </Button>
            {csvFileName && <Typography variant="body2">{csvFileName}</Typography>}
            {csvGroups && (
              <Typography variant="body2">
                Ready to apply {csvGroups.length} groups and{' '}
                {csvGroups.reduce((count, group) => count + group.locationIds.length, 0)} locations.
              </Typography>
            )}
            <Button color="primary" disabled={!csvGroups || isUploading} onClick={uploadGroups} variant="contained">
              {isUploading ? 'Updating...' : 'Update groups'}
            </Button>
          </div>
          {csvMessage && (
            <Typography color={csvMessage.includes('successfully') ? 'primary' : 'error'} role="status">
              {csvMessage}
            </Typography>
          )}
        </Paper>
      </main>
    </div>
  );
};

export default AnnouncementGroups;
